import { useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { Settings, LogOut, UserRound, Hand, Undo2 } from "lucide-react";
import { readImpersonationOrigin, endImpersonation } from "@/components/ImpersonationBanner";
import { skipNextUnloadPrompt, requestLeave } from "@/lib/leaveRegistry";
import { useAuth } from "@/context/AuthContext";
import { useLock } from "@/context/LockContext";
import HealthPulseIcon from "@/components/HealthPulseIcon";
import ProfilePhoto from "@/components/ui/ProfilePhoto";
import QuitDeskDialog from "@/components/QuitDeskDialog";
import { clearUnsavedChanges } from "@/lib/unsavedChanges";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * HeaderProfileGroup — Spec 2026-02-XX rev37 (refonte topbar).
 *
 * Chaque élément est un avatar rond 32×32 avec fond plein saturé de sa
 * couleur sémantique et une icône blanche contrastée :
 *   • Alerte docs corrompus : rouge  #EF4444  (Admin only, masqué si 0)
 *   • Notifications           : jaune  #F9BC26  (badge overlay)
 *   • Bye bye                 : vert   #22C55E  (👋 blanc)
 *   • Avatar utilisateur      : rond bleu inchangé (déjà dans le style)
 *   • Paramètres              : bleu   #0078D4
 *
 * Les badges numériques (alerte / notifs) sont des petits cercles
 * sombres avec bordure de la couleur du parent, superposés au coin
 * haut-droit, cohérents avec le style déjà utilisé ailleurs dans l'app.
 */
export default function HeaderProfileGroup() {
  const { user, logout } = useAuth();
  const { lock, enabled: lockEnabled } = useLock();
  const navigate = useNavigate();
  const [quitOpen, setQuitOpen] = useState(false);

  const fullName = ((user?.first_name || "") + " " + (user?.last_name || "")).trim();

  const handleByeBye = () => requestLeave("desk", () => {
    if (lockEnabled) {
      lock();
      return;
    }
    const departure = new Date();
    const departureHHMM = departure.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    navigate("/bye-bye", {
      replace: true,
      state: {
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        departure_time: departureHHMM,
        departure_iso: departure.toISOString(),
      },
    });
  });

  const handleLogout = async () => {
    if (user?.email) {
      try { localStorage.setItem("ledesk_last_email", user.email); } catch {}
    }
    clearUnsavedChanges();
    await logout();
    navigate("/connexion", { replace: true });
  };

  const slot = { width: 34, height: 30, borderRadius: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", transition: "background-color .15s" };
  const canSettings = !user?.access_rights || (user.access_rights.settings || []).includes("view");
  // iter424 pt 8-9 — session d'un autre utilisateur : capsule ambre + ↩ retour direct (aucune fenêtre, aucun beforeunload)
  const imp = readImpersonationOrigin();
  const returnToMine = () => { if (endImpersonation()) { clearUnsavedChanges(); skipNextUnloadPrompt(); window.location.href = "/"; } };
  return (
    <TooltipProvider delayDuration={150}>
      <style>{`.topbar-slot:hover{background:#1c1c22!important}`}</style>
      {/* Ch. 5 — capsule unique : Configuration · Santé · Main · Profil (plus de cloche ni d'alerte documents : notifications par e-mail uniquement). */}
      <div className="flex items-center" style={{ gap: 2, padding: 4, background: imp ? "#1C170F" : "#111114", border: imp ? "1px solid #F59E0B" : "0.5px solid #2a2a30", borderRadius: 22 }} data-testid="header-profile-group" data-impersonating={imp ? "1" : undefined}>
        {canSettings && <Tooltip><TooltipTrigger asChild>
          <button type="button" onClick={() => navigate("/parametres")} aria-label="Paramètres" className="topbar-slot" style={slot} data-testid="topbar-settings-btn"><Settings size={18} style={{ color: "#7fb4ec" }} /></button>
        </TooltipTrigger><TooltipContent side="bottom" sideOffset={8}>Paramètres</TooltipContent></Tooltip>}
        <HealthPulseIcon />
        <Tooltip><TooltipTrigger asChild>
          <button type="button" onClick={handleByeBye} aria-label={"Bye bye " + (user?.first_name || "")} className="topbar-slot" style={slot} data-testid="btn-bye-bye"><Hand size={18} style={{ color: "#E0A42E" }} /></button>
        </TooltipTrigger><TooltipContent side="bottom" sideOffset={8}>{"Bye bye " + (user?.first_name || "")}</TooltipContent></Tooltip>
        <DropdownMenu>
          <Tooltip><TooltipTrigger asChild><DropdownMenuTrigger asChild>
            <button type="button" className="topbar-slot relative focus:outline-none" style={{ ...slot, width: 38, height: 34, borderRadius: 17 }} data-testid="topbar-user-block" aria-label={"Menu du profil de " + fullName}>
              <ProfilePhoto user={user} size={30} testid="topbar-avatar" />
              <span className="absolute" style={{ width: 10, height: 10, borderRadius: "50%", background: "#4fc79c", border: "2px solid #111114", right: 2, bottom: 0 }} data-testid="topbar-presence" />
            </button>
          </DropdownMenuTrigger></TooltipTrigger><TooltipContent side="bottom" sideOffset={8}>{fullName || user?.first_name || ""}</TooltipContent></Tooltip>
          <DropdownMenuContent align="end" sideOffset={10} className="min-w-[210px]" data-testid="profile-menu">
            <DropdownMenuItem onClick={() => navigate("/parametres?tab=account")} className="gap-2 cursor-pointer" data-testid="menu-profile"><UserRound size={16} />Mon profil</DropdownMenuItem>
            <DropdownMenuSeparator style={{ height: 0.5, background: "#2a2a30" }} />
            <DropdownMenuItem onClick={() => requestLeave("desk", () => setQuitOpen(true))} className="gap-2 cursor-pointer" style={{ color: "#ef8a7a" }} onMouseEnter={(e) => { e.currentTarget.style.background = "#2a1a1a"; }} onMouseLeave={(e) => { e.currentTarget.style.background = ""; }} data-testid="menu-logout"><LogOut size={16} color="#ef8a7a" />Quitter Le Desk</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {imp && <>
          <span aria-hidden="true" style={{ width: 1, height: 18, background: "#4A3A1A", margin: "0 4px" }} data-testid="topbar-impersonation-sep" />
          <Tooltip><TooltipTrigger asChild>
            <button type="button" onClick={returnToMine} aria-label={`Revenir à mon compte (${imp.admin_name || ""})`} className="topbar-slot" style={slot} data-testid="impersonation-return-btn"><Undo2 size={16} style={{ color: "#F59E0B" }} /></button>
          </TooltipTrigger><TooltipContent side="bottom" sideOffset={8}>{`Revenir à mon compte (${imp.admin_name || ""})`}</TooltipContent></Tooltip>
        </>}
        <QuitDeskDialog open={quitOpen} onStay={() => setQuitOpen(false)} onQuit={() => { setQuitOpen(false); handleLogout(); }} />
      </div>
    </TooltipProvider>
  );
}
